/**
 * morse.mjs — the Morse light, as timing rather than as text.
 *
 * FOUNDER, 2026-08-13: "we can make a morse light generator and messge maker with speed control",
 * then "6 words per minute" when asked what the examination requires.
 *
 * Chapter 17 is the only chapter on the 2M ladder that feeds a PRACTICAL examination, and a practical
 * cannot be taught by reading. Its apply rung says "Make and read the required signals"; this is the
 * instrument that lets a candidate actually do it.
 *
 * ── THE SPEED IS A DECLARED RULING, NOT A DEFAULT ─────────────────────────────────────────────
 * 6 WPM is the founder's answer to a direct question about what DG Shipping requires. It is held as a
 * named constant with its source, because a number chosen by a builder and left in a config becomes
 * the standard by accident — the same trap as the missing plates and the wrong datum. The control
 * still moves; the DECLARED speed is what a candidate is told to practise at.
 *
 * ── WHY PARIS IS THE ONLY HONEST WAY TO CHECK THE TIMING ──────────────────────────────────────
 * A Morse table looks right when it is wrong. Get one dash short and the light still flashes
 * convincingly, and a learner drills a mistake at 6 words a minute until it is muscle memory.
 *
 * The standard fixes this: the word PARIS is DEFINED as 50 units, and that is what "words per minute"
 * counts. So the alphabet and the spacing rules together are checkable against a single number, which
 * tests every gap rule and five letters at once.
 *
 * The number needs care, and it caught me on the first run. PARIS is 43 units of LIGHT AND DARK; the
 * standard's 50 includes the seven-unit word space that follows it. Both are named here — totalUnits
 * and unitsPerWord — because asserting only the 50 would be satisfied by an implementation that
 * wrongly pads every message with trailing darkness. gate-morse.mjs asserts both.
 *
 * Timing, ITU-R M.1677:  dot 1 · dash 3 · gap within a character 1 · between characters 3 · between
 * words 7. One unit at W words per minute is 1200/W milliseconds — 200 ms at six.
 */

/** ITU-R M.1677-1. Letters and figures only: the signals practical does not send punctuation. */
export const ALPHABET = Object.freeze({
  A: '.-',    B: '-...',  C: '-.-.',  D: '-..',   E: '.',     F: '..-.',
  G: '--.',   H: '....',  I: '..',    J: '.---',  K: '-.-',   L: '.-..',
  M: '--',    N: '-.',    O: '---',   P: '.--.',  Q: '--.-',  R: '.-.',
  S: '...',   T: '-',     U: '..-',   V: '...-',  W: '.--',   X: '-..-',
  Y: '-.--',  Z: '--..',
  0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-',
  5: '.....', 6: '-....', 7: '--...', 8: '---..', 9: '----.',
});

/** The founder's ruling, 2026-08-13. Held here so nothing has to guess it. */
export const EXAM_WPM = 6;
export const EXAM_WPM_SOURCE = "Founder's ruling, 2026-08-13, asked what DG Shipping requires for the signals practical.";

export const UNITS = Object.freeze({ dot: 1, dash: 3, gapElement: 1, gapChar: 3, gapWord: 7 });
export const unitMs = (wpm) => 1200 / wpm;

/**
 * Encode text to a flat list of {on, units} — the lamp's whole script. Returned as timing rather than
 * as dots and dashes because the lamp does not know what a dash is: it knows how long to stay lit,
 * and marking a candidate's reading means comparing what was SENT with what he wrote.
 */
export function toTiming(text) {
  const words = String(text || '').toUpperCase().trim().split(/\s+/).filter(Boolean);
  const out = [];
  words.forEach((word, wi) => {
    const chars = [...word].filter((c) => ALPHABET[c]);
    chars.forEach((c, ci) => {
      const el = ALPHABET[c];
      [...el].forEach((mark, mi) => {
        out.push({ on: true, units: mark === '-' ? UNITS.dash : UNITS.dot });
        if (mi < el.length - 1) out.push({ on: false, units: UNITS.gapElement });
      });
      if (ci < chars.length - 1) out.push({ on: false, units: UNITS.gapChar });
    });
    if (wi < words.length - 1) out.push({ on: false, units: UNITS.gapWord });
  });
  return out;
}

/** Total units of the message itself — the light's on/off script, with no trailing silence. */
export const totalUnits = (text) => toTiming(text).reduce((t, s) => t + s.units, 0);

/**
 * Units of a message AS A UNIT OF SENDING RATE — the message plus the word space that follows it.
 *
 * This distinction is where a Morse implementation usually goes wrong, and it caught me: PARIS is 43
 * units of light and dark, and the standard's famous 50 INCLUDES the seven-unit word space after it.
 * Both numbers are right and they answer different questions. 43 is how long the lamp is doing
 * something; 50 is what "words per minute" counts, which is why PARIS repeats once every ten seconds
 * at six words a minute rather than every 8.6.
 *
 * The first version of the gate asserted totalUnits('PARIS') === 50 and went red against correct
 * code. Naming both quantities is the fix — an implementation that silently added seven units of
 * darkness to the end of every message would have made the assertion pass and the lamp wrong.
 */
export const unitsPerWord = (text) => totalUnits(text) + UNITS.gapWord;

/** How long a message takes at a given speed, in seconds. */
export const durationSec = (text, wpm) => (totalUnits(text) * unitMs(wpm)) / 1000;
/** How long one full sending CYCLE takes, message plus the word space — the rate a watch can check. */
export const cycleSec = (text, wpm) => (unitsPerWord(text) * unitMs(wpm)) / 1000;

/** Dots and dashes, for a learner who wants to see what he just watched. */
export const toMorse = (text) => String(text || '').toUpperCase().trim().split(/\s+/)
  .map((w) => [...w].filter((c) => ALPHABET[c]).map((c) => ALPHABET[c]).join(' ')).join(' / ');

/**
 * Marking is a COMPARISON, never a judgement. The lamp knows exactly what it sent, so a candidate's
 * reading is scored character by character against it — and the per-character detail is returned so
 * he can see WHERE he lost it, which is the whole value. A score with no position is a grade; a score
 * with positions is a lesson.
 */
export function mark(sent, received) {
  const norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
  const a = norm(sent), b = norm(received);
  const n = Math.max(a.length, b.length);
  const detail = [];
  let correct = 0;
  for (let i = 0; i < n; i += 1) {
    const ok = a[i] !== undefined && a[i] === b[i];
    if (ok) correct += 1;
    detail.push({ i, sent: a[i] ?? '', got: b[i] ?? '', ok });
  }
  return {
    sent: a, received: b, correct, of: a.length,
    /* Scored against what was SENT, not against the longer of the two — a candidate who types extra
       characters has not earned a higher denominator. */
    pct: a.length ? Math.round((100 * correct) / a.length) : 0,
    detail,
  };
}

/**
 * ── THE DRILL LIST, AND THE MEANINGS I HAD TO TAKE BACK ───────────────────────────────────────
 *
 * The first version of this list was written from my own memory: K = "I wish to communicate with
 * you", AA = "the call for an unknown station", RPT = "repeat", DE = "from". Those are real signals
 * and, so far as I know, correctly glossed — and not one of them appears in 2m-signals.json, the
 * source chapter 17 actually declares. A meaning I supply from memory arrives on the candidate's
 * screen wearing exactly the same clothes as a meaning the International Code supplies, and he has
 * no way to tell them apart. That is the whole failure mode this codebase exists to refuse.
 *
 * So every meaning below is QUOTED from the chapter's declared corpus with its citation, and the
 * letters the corpus does not carry are gone. What is left is better than what I invented: the
 * twelve single-letter signals the syllabus names, which IS the chapter's remember rung — "State the
 * key single-letter signals and their meanings" — now drillable by light.
 *
 * The corpus is also the warrant for this lab existing at all. Its single-letter entry opens
 * "Single flags (or their MORSE LIGHT/sound equivalents)" — the chapter declares the medium.
 *
 * Plain-language words carry no meaning claim, because none is needed: the practical sends plain
 * language, and a word is its own meaning.
 */
const ICS_LETTERS = 'International Code of Signals (IMO, as amended), Single-Letter Signals — via 2m-signals.json';

export const DRILL = Object.freeze([
  /* The distress group. SOS is quoted from the chapter's own list of recognised distress signals,
     which writes it out as ···———··· — an independent confirmation of the three letters below. */
  { text: 'SOS', means: 'A recognised distress signal, by any method.',
    source: 'COLREG 1972 Annex IV (as amended); SOLAS Ch. V Reg. 29 — via 2m-signals.json' },
  { text: 'NC', means: 'I am in distress and require immediate assistance.',
    source: 'International Code of Signals, Distress section; COLREG Annex IV — via 2m-signals.json' },

  /* The twelve single letters the syllabus names, in the corpus's own words. */
  { text: 'A', means: 'Diver down, keep clear, slow speed.', source: ICS_LETTERS },
  { text: 'B', means: 'Carrying, loading or discharging dangerous goods.', source: ICS_LETTERS },
  { text: 'D', means: 'Keep clear, manoeuvring with difficulty.', source: ICS_LETTERS },
  { text: 'G', means: 'I require a pilot.', source: ICS_LETTERS },
  { text: 'H', means: 'I have a pilot on board.', source: ICS_LETTERS },
  { text: 'L', means: 'Stop your vessel instantly.', source: ICS_LETTERS },
  { text: 'O', means: 'Man overboard.', source: ICS_LETTERS },
  { text: 'P', means: 'In harbour: vessel about to proceed to sea, all persons report on board.', source: ICS_LETTERS },
  { text: 'Q', means: 'My vessel is healthy, I request free pratique.', source: ICS_LETTERS },
  { text: 'U', means: 'You are running into danger.', source: ICS_LETTERS },
  { text: 'V', means: 'I require assistance.', source: ICS_LETTERS },
  { text: 'W', means: 'I require medical assistance.', source: ICS_LETTERS },

  /* The timing word. Its meaning is a fact about the STANDARD, not about the Code — cited as such. */
  { text: 'PARIS', means: 'The timing word: 50 units with its word space, which is what defines words per minute.',
    source: 'ITU-R M.1677 timing; see unitsPerWord() and gate-morse.mjs' },

  /* Plain language. No meaning asserted, because a word is its own meaning. */
  { text: 'ANCHOR', means: null, source: null },
  { text: 'PILOT ON BOARD', means: null, source: null },
]);
